<template>
  <Teleport defer to="ion-app">
    <ion-modal
      id="force-update-modal"
      ref="modal"
      :swipe-to-close="false"
      :can-dismiss="false"
      class="force-update-modal"
    >
      <div class="force-update-modal-wrapper">
        <div class="force-update-modal-content">
          <!-- Icon -->
          <div class="force-update-icon">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="24" cy="24" r="20" fill="#FEC629" opacity="0.2" />
              <path
                d="M24 14V26M24 32H24.02"
                stroke="#FEC629"
                stroke-width="3"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <!-- Message -->
          <p class="force-update-text">
            {{ displayMessage }}
          </p>

          <!-- Version info (if available) -->
          <p v-if="minimumVersion" class="force-update-version">
            Minimum version: {{ minimumVersion }}
          </p>

          <!-- Buttons -->
          <div class="force-update-buttons">
            <!-- Update Now button -->
            <button class="force-update-button force-update-button-primary" @click="handleUpdate">
              Update Now
            </button>
          </div>
        </div>
      </div>
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';

export default {
  name: 'ForceUpdateModal',
  components: {
    IonModal,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: 'A new version is available. Please update to continue using the app.',
    },
    storeUrl: {
      type: String,
      default: '',
    },
    minimumVersion: {
      type: String,
      default: '',
    },
  },
  computed: {
    displayMessage() {
      return this.message || 'A new version is available. Please update to continue using the app.';
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.$refs.modal?.$el?.present();
      } else {
        this.$refs.modal?.$el?.dismiss();
      }
    },
  },
  mounted() {
    if (this.isOpen) {
      this.$nextTick(() => this.$refs.modal?.$el?.present());
    }
  },
  methods: {
    handleUpdate() {
      // Emit update event with store URL
      this.$emit('update', { storeUrl: this.storeUrl });
    },
  },
};
</script>

<style scoped>
ion-modal#force-update-modal.force-update-modal {
  --width: 327px;
  --height: fit-content;
  --border-radius: 12px;
  z-index: 9999; /* Ensure modal appears above splash screen */
  --backdrop-opacity: 0.4;
  --ion-backdrop-color: rgba(0, 0, 0, 0.4);
}

/* Ensure modal wrapper is visible */
.force-update-modal-wrapper {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  position: relative;
  z-index: 10000;
  pointer-events: none; /* Allow clicks to pass through wrapper */
  background: transparent;
  margin: 0;
  overflow: hidden;
}

.force-update-modal-content {
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  width: 327px;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  box-shadow: 0px 4px 20px 4px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10001;
  pointer-events: auto; /* Re-enable pointer events on content */
}

.force-update-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.force-update-text {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 25px;
  color: #303030;
  text-align: center;
  margin: 0;
  width: 100%;
}

.force-update-version {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: #666666;
  text-align: center;
  margin: 0;
  width: 100%;
}

.force-update-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  margin-top: 8px;
}

.force-update-button {
  background: #fec629;
  border: none;
  border-radius: 100px;
  padding: 12px 20px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  color: #21201e;
  text-align: center;
  cursor: pointer;
  letter-spacing: -0.48px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.force-update-button:active {
  opacity: 0.8;
}
</style>

<style>
/* Global styles for ion-modal to ensure visibility */
ion-modal#force-update-modal.force-update-modal {
  --backdrop-opacity: 0.4 !important;
  --ion-backdrop-color: rgba(0, 0, 0, 0.4) !important;
  background: transparent !important;
  background-color: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

/* Target the shadow DOM root to remove yellow background */
ion-modal#force-update-modal.force-update-modal::part(backdrop),
ion-modal#force-update-modal.force-update-modal::part(content) {
  background: transparent !important;
  background-color: transparent !important;
}

/* Ensure modal content wrapper is visible and transparent */
ion-modal#force-update-modal.force-update-modal .modal-wrapper {
  background: transparent !important;
  background-color: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  overflow: visible !important;
  padding: 0 !important;
  margin: 0 !important;
  max-width: 100% !important;
  max-height: 100% !important;
  pointer-events: none !important;
}

/* Remove any default Ionic modal styling that might cause yellow stripe */
ion-modal#force-update-modal.force-update-modal {
  --width: 100% !important;
  --max-width: 100% !important;
}

/* Ensure wrapper and modal elements are transparent, but keep content white */
ion-modal#force-update-modal.force-update-modal .modal-wrapper,
ion-modal#force-update-modal.force-update-modal .force-update-modal-wrapper {
  background-color: transparent !important;
  background: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

/* Force remove yellow from modal shadow DOM and all children */
ion-modal#force-update-modal.force-update-modal .ion-delegate-host,
ion-modal#force-update-modal.force-update-modal .ion-page {
  background-color: transparent !important;
  background: transparent !important;
  --background: transparent !important;
  --ion-background-color: transparent !important;
}

/* Re-apply white background to content */
ion-modal#force-update-modal.force-update-modal .force-update-modal-content {
  background-color: #ffffff !important;
  background: #ffffff !important;
}

/* Gray backdrop overlay */
ion-modal#force-update-modal.force-update-modal ion-backdrop {
  background: rgba(0, 0, 0, 1) !important;
  background-color: rgba(0, 0, 0, 1) !important;
  --background: rgba(0, 0, 0, 1) !important;
  --ion-backdrop-color: rgba(0, 0, 0, 1) !important;
  opacity: 0.4 !important;
  filter: none !important;
  mix-blend-mode: normal !important;
  display: block !important;
  visibility: visible !important;
}

/* Override any yellow that might be inherited */
ion-modal#force-update-modal.force-update-modal ion-backdrop::before,
ion-modal#force-update-modal.force-update-modal ion-backdrop::after {
  background: rgba(0, 0, 0, 0.3) !important;
  background-color: rgba(0, 0, 0, 0.3) !important;
  opacity: 0.3 !important;
}

/* Ensure modal content is visible */
ion-modal#force-update-modal.force-update-modal .force-update-modal-content {
  background: #ffffff !important;
  position: relative !important;
  z-index: 10001 !important;
  box-shadow: 0px 4px 20px 4px rgba(0, 0, 0, 0.3) !important;
  display: flex !important;
  visibility: visible !important;
  opacity: 1 !important;
}
</style>
